import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateMpManualSessionInput,
  CreateMpPlaceInput,
  CreateMpWebhookTokenInput,
  MpChainVerifyResult,
  MpPlace,
  MpRawEvent,
  MpReport,
  MpSession,
  MpSessionEdit,
  MpThresholds,
  MpWebhookToken,
  MpWebhookTokenCreated,
  MpYearLock,
  UpdateMpPlaceInput,
  UpdateMpSessionInput,
  UpdateMpThresholdsInput,
} from '@accounting/shared';
import { api } from '../lib/api';

// Not company-scoped: participation belongs to the signed-in owner, so no key
// carries a company id.
export const participationPlacesKey = ['participation', 'places'] as const;
export const participationTokensKey = ['participation', 'webhook-tokens'] as const;
export const participationRecentEventsKey = ['participation', 'events', 'recent'] as const;
export const participationChainKey = ['participation', 'events', 'verify-chain'] as const;

export function useParticipationPlaces() {
  return useQuery({
    queryKey: participationPlacesKey,
    queryFn: () => api.get<{ places: MpPlace[] }>('/participation/places').then((r) => r.places),
  });
}

export function useCreateParticipationPlace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMpPlaceInput) =>
      api.post<{ place: MpPlace }>('/participation/places', input).then((r) => r.place),
    onSuccess: () => qc.invalidateQueries({ queryKey: participationPlacesKey }),
  });
}

export function useUpdateParticipationPlace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { placeId: string; input: UpdateMpPlaceInput }) =>
      api
        .patch<{ place: MpPlace }>(`/participation/places/${vars.placeId}`, vars.input)
        .then((r) => r.place),
    onSuccess: () => qc.invalidateQueries({ queryKey: participationPlacesKey }),
  });
}

export function useParticipationTokens() {
  return useQuery({
    queryKey: participationTokensKey,
    queryFn: () =>
      api.get<{ tokens: MpWebhookToken[] }>('/participation/webhook-tokens').then((r) => r.tokens),
  });
}

/** The response is the only time the secret is ever available. */
export function useCreateParticipationToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMpWebhookTokenInput) =>
      api.post<MpWebhookTokenCreated>('/participation/webhook-tokens', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: participationTokensKey }),
  });
}

export function useRevokeParticipationToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: string) =>
      api
        .post<{ token: MpWebhookToken }>(`/participation/webhook-tokens/${tokenId}/revoke`, {})
        .then((r) => r.token),
    onSuccess: () => qc.invalidateQueries({ queryKey: participationTokensKey }),
  });
}

export function useParticipationRecentEvents() {
  return useQuery({
    queryKey: participationRecentEventsKey,
    queryFn: () =>
      api.get<{ events: MpRawEvent[] }>('/participation/events/recent').then((r) => r.events),
  });
}

export function useParticipationChain() {
  return useQuery({
    queryKey: participationChainKey,
    queryFn: () => api.get<MpChainVerifyResult>('/participation/events/verify-chain'),
  });
}

// ── Sessions, report, thresholds, year lock ─────────────────────────────────

export const participationExceptionsKey = ['participation', 'exceptions'] as const;
export const participationReportKey = (year: number) => ['participation', 'report', year] as const;
export const participationSessionKey = (id: string) => ['participation', 'session', id] as const;
export const participationThresholdsKey = ['participation', 'thresholds'] as const;
export const participationYearLockKey = ['participation', 'year-lock'] as const;

/**
 * The sessions that need a person. The nav badge counts this same payload
 * (hooks/useNudges.ts), so the badge and the page cannot disagree.
 */
export function useParticipationExceptions(options: { staleTime?: number; quiet?: boolean } = {}) {
  return useQuery({
    queryKey: participationExceptionsKey,
    staleTime: options.staleTime,
    retry: options.quiet ? false : undefined,
    refetchOnWindowFocus: options.quiet ? false : undefined,
    queryFn: () =>
      api.get<{ exceptions: MpSession[] }>('/participation/exceptions').then((r) => r.exceptions),
  });
}

export function useParticipationReport(year: number) {
  return useQuery({
    queryKey: participationReportKey(year),
    queryFn: () =>
      api.get<{ report: MpReport }>('/participation/report', { year: String(year) }).then((r) => r.report),
  });
}

export function useParticipationSession(sessionId: string | null) {
  return useQuery({
    queryKey: participationSessionKey(sessionId ?? 'none'),
    enabled: sessionId !== null,
    queryFn: () =>
      api.get<{ session: MpSession; edits: MpSessionEdit[] }>(`/participation/sessions/${sessionId}`),
  });
}

/** Any hand change moves totals, the exception count and the edit list at once. */
function useInvalidateSessions() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: participationExceptionsKey });
    void qc.invalidateQueries({ queryKey: ['participation', 'report'] });
    void qc.invalidateQueries({ queryKey: ['participation', 'session'] });
  };
}

export function useUpdateParticipationSession() {
  const invalidate = useInvalidateSessions();
  return useMutation({
    mutationFn: (vars: { sessionId: string; input: UpdateMpSessionInput }) =>
      api
        .patch<{ session: MpSession }>(`/participation/sessions/${vars.sessionId}`, vars.input)
        .then((r) => r.session),
    onSuccess: invalidate,
  });
}

export function useCreateManualParticipationSession() {
  const invalidate = useInvalidateSessions();
  return useMutation({
    mutationFn: (input: CreateMpManualSessionInput) =>
      api.post<{ session: MpSession }>('/participation/sessions', input).then((r) => r.session),
    onSuccess: invalidate,
  });
}

export function useParticipationThresholds() {
  return useQuery({
    queryKey: participationThresholdsKey,
    queryFn: () =>
      api.get<{ thresholds: MpThresholds }>('/participation/thresholds').then((r) => r.thresholds),
  });
}

export function useUpdateParticipationThresholds() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMpThresholdsInput) =>
      api.put<{ thresholds: MpThresholds }>('/participation/thresholds', input).then((r) => r.thresholds),
    // The time zone decides which day and year a session falls in.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['participation'] }),
  });
}

export function useParticipationYearLock() {
  return useQuery({
    queryKey: participationYearLockKey,
    queryFn: () => api.get<{ yearLock: MpYearLock }>('/participation/year-lock').then((r) => r.yearLock),
  });
}

export function useLockParticipationYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (year: number) =>
      api.post<{ yearLock: MpYearLock }>('/participation/year-lock/lock', { year }).then((r) => r.yearLock),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['participation'] }),
  });
}

export function useUnlockParticipationYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { year: number; reason: string }) =>
      api.post<{ yearLock: MpYearLock }>('/participation/year-lock/unlock', vars).then((r) => r.yearLock),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['participation'] }),
  });
}
